import { isPlatformBrowser } from '@angular/common';
import { Injectable, inject, PLATFORM_ID, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from '../store/storage-service';
import { UploadTaskCreationStatus } from '../upload-task/upload-task-service';
import { LoggerService } from '../logging/logger.service';


@Injectable({
  providedIn: 'root'
})
export class UIService {

  private readonly translateService = inject(TranslateService);
  private readonly storageService = inject(StorageService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly logger = inject(LoggerService);
  private readonly languageStorageKey = 'language';

  /**
   * Signals to manage UI state:
   * - toggleFeedbackForm: Indicates whether the feedback form is visible.
   * - validTemplateFilling: Indicates whether the template filling for create uploadtask is valid.
   * - resUploadTaskCreated: Holds the status of the upload task creation.
   * - elementsLoading: Indicates whether UI elements are in a loading state.
   */
  validTemplateFilling = signal<boolean>(false);
  resUploadTaskCreated = signal<UploadTaskCreationStatus>(UploadTaskCreationStatus.NotStarted);
  elementsLoading = signal<boolean>(false);

  constructor() {}
  /**
   * Initializes the application's language based on the user's previous selection stored in local storage, or defaults to German if no selection is found. The selected language is then set in the TranslateService.
   * @returns The selected language, either 'de' or 'en'.
   */
  initializeLanguage(): 'de' | 'en' {
    const language = this.isBrowser
      ? this.storageService.getLocal(this.languageStorageKey)
      : null;
    const selectedLanguage = language === 'en' ? 'en' : 'de';
    this.logger.info('UIService', 'Language initialized', { selectedLanguage });

    this.translateService.use(selectedLanguage);
    return selectedLanguage;
  }

  /**
   * Changes the application's language and persists the selection in local storage if running in a browser environment.
   * @param language 
   */
  changeLanguage(language: 'de' | 'en'): void {
    this.logger.info('UIService', 'Changing application language', { newLanguage: language });
    this.translateService.use(language);

    if (this.isBrowser) {
      this.storageService.setLocal(this.languageStorageKey, language);
      this.logger.debug('UIService', 'Language preference saved to storage');
    }
  }

  /**
   * Toggles the visibility of the feedback form.
   */
  toggleFeedbackFormVisibility(currentState: boolean): boolean {
    const newState = !currentState;
    this.logger.debug('UIService', 'Toggling feedback form visibility', { newState });
    return newState;
  }

  /**
   * Toggles the state of the validTemplateFilling signal.
   */
  toggleValidTemplateFilling(): void {
    const newState = !this.validTemplateFilling();
    this.validTemplateFilling.set(newState);
    this.logger.debug('UIService', 'Toggling valid template filling state', { newState });
  }

  /**
   * Sets the status of the resUploadTaskCreated signal.
   * @param status The new status of the upload task.
   */
  setResUploadTaskCreated(status: UploadTaskCreationStatus): void {
    this.resUploadTaskCreated.set(status);
    this.logger.debug('UIService', 'Upload task creation status updated', { status });
  } 
}
